/**
 * ArtistDrawer — slide-in artist profile side sheet.
 *
 * Opens from the right when a listener clicks a cover-wall tile. Shows:
 *   • artist avatar + name + verified/AI badge
 *   • bio snippet, track + follower counts
 *   • top 3 tracks
 *   • "View full profile" CTA
 *
 * Hand-rolled (no Radix Sheet) so it renders reliably on every mobile
 * browser — previous Radix Sheet + tailwindcss-animate combo left the
 * drawer off-screen on some iOS Safari builds (translateX(100%) stuck
 * when the slide-in animation didn't fire), showing only the backdrop.
 * This version uses plain CSS transforms and a high z-index so it's
 * guaranteed to be visible the moment `open === true`.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { CheckCircle2, Bot, Users, Music2, ExternalLink, Loader2, X } from 'lucide-react';
import { getProfilePicUrl } from '../utils/profilePic';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function avatarUrl(profile_picture, user_id) {
  return getProfilePicUrl(profile_picture, user_id);
}

export default function ArtistDrawer({ userId, open, onClose }) {
  const [profile, setProfile] = useState(null);
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!userId || !open) return;
    let alive = true;
    setLoading(true);
    setProfile(null);
    setTracks([]);
    (async () => {
      try {
        const [profRes, tracksRes] = await Promise.all([
          axios.get(`${API}/users/${userId}`).catch(() => null),
          axios.get(`${API}/tracks/user/${userId}`).catch(() => ({ data: [] })),
        ]);
        if (!alive) return;
        setProfile(profRes?.data || null);
        setTracks((tracksRes?.data || []).slice(0, 3));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [userId, open]);

  // Lock body scroll while the drawer is open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  const gotoFullProfile = () => {
    if (!profile) return;
    onClose?.();
    if (profile.username) navigate(`/u/${profile.username}`);
    else navigate(`/profile/${profile.id}`);
  };

  // Keep mounted when open OR during closing animation — simplest: always
  // render if open (unmount after close). Close is instant from state flip.
  if (!open) return null;

  return (
    <div
      className="fixed inset-0"
      style={{ zIndex: 9999 }}
      data-testid="artist-drawer-root"
    >
      {/* Backdrop */}
      <button
        type="button"
        aria-label="Close artist preview"
        onClick={onClose}
        className="absolute inset-0 bg-black/80 cursor-default"
        data-testid="artist-drawer-backdrop"
      />

      {/* Panel — fixed right, full width on mobile, capped on desktop */}
      <aside
        role="dialog"
        aria-modal="true"
        aria-label="Artist preview"
        className="absolute top-0 right-0 h-full w-full sm:max-w-md bg-[#070707] border-l border-[#1a1a1a] text-white overflow-y-auto"
        style={{
          // Explicit transform — guaranteed on-screen regardless of CSS
          // animation support. Drawer slides in via the [animation] below.
          transform: 'translateX(0)',
          animation: 'artistDrawerSlideIn 220ms ease-out',
          WebkitOverflowScrolling: 'touch',
        }}
        data-testid="artist-drawer"
      >
        {/* Close button */}
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 z-30 w-10 h-10 rounded-full bg-black/70 hover:bg-black text-white flex items-center justify-center backdrop-blur-sm border border-white/10 transition-colors"
          aria-label="Close artist preview"
          data-testid="artist-drawer-close"
        >
          <X size={20} />
        </button>

        {/* Hero cover strip */}
        <div
          className="relative h-32 bg-gradient-to-br from-[#FFDA33]/15 via-[#00CCFF]/10 to-transparent overflow-hidden"
          data-testid="artist-drawer-cover"
        >
          {profile?.cover_image && (
            <img
              src={
                profile.cover_image.startsWith('http')
                  ? profile.cover_image
                  : `${BACKEND_URL}/api/profile/cover/${profile.cover_image}`
              }
              alt=""
              className="absolute inset-0 w-full h-full object-cover opacity-60"
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-[#070707] via-[#070707]/50 to-transparent" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(255,218,51,0.12),transparent_70%)]" />
        </div>

        {loading && (
          <div className="flex items-center justify-center py-20 text-[#888]">
            <Loader2 size={20} className="animate-spin mr-2" /> Loading artist…
          </div>
        )}

        {!loading && profile && (
          <div className="px-5 -mt-10 pb-8 relative z-10" data-testid="artist-drawer-body">
            {/* Avatar */}
            <div
              className="w-24 h-24 rounded-full border-4 border-[#070707] bg-[#111] overflow-hidden shadow-xl flex items-center justify-center"
              data-testid="artist-drawer-avatar"
            >
              {avatarUrl(profile.profile_picture, profile.id) ? (
                <img
                  src={avatarUrl(profile.profile_picture, profile.id)}
                  alt={profile.username}
                  className="w-full h-full object-cover"
                  data-testid="artist-drawer-avatar-img"
                  onLoad={(e) => {
                    const img = e.currentTarget;
                    if (img.naturalWidth < 40 || img.naturalHeight < 40) {
                      const parent = img.parentElement;
                      img.remove();
                      if (parent) {
                        parent.innerHTML =
                          `<div class="w-full h-full flex items-center justify-center text-[#FFDA33] font-bold text-3xl">${
                            (profile.username || '?').slice(0, 1).toUpperCase()
                          }</div>`;
                      }
                    }
                  }}
                  onError={(e) => {
                    const parent = e.currentTarget.parentElement;
                    e.currentTarget.remove();
                    if (parent) {
                      parent.innerHTML =
                        `<div class="w-full h-full flex items-center justify-center text-[#FFDA33] font-bold text-3xl">${
                          (profile.username || '?').slice(0, 1).toUpperCase()
                        }</div>`;
                    }
                  }}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-[#FFDA33] font-bold text-3xl">
                  {(profile.username || '?').slice(0, 1).toUpperCase()}
                </div>
              )}
            </div>

            {/* Name + badges */}
            <div className="mt-3">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="text-2xl font-['Chakra_Petch'] text-white truncate" data-testid="artist-drawer-name">
                  {profile.username}
                </h3>
                {profile.is_verified && (
                  <CheckCircle2 size={18} className="text-[#00CCFF]" />
                )}
                {profile.is_ai_artist && (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-[#A855F7]/15 border border-[#A855F7]/40 text-[9px] font-mono uppercase text-[#A855F7]">
                    <Bot size={9} /> AI
                  </span>
                )}
                {profile.is_artist && !profile.is_ai_artist && (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-[#FFDA33]/15 border border-[#FFDA33]/40 text-[9px] font-mono uppercase text-[#FFDA33]">
                    Artist
                  </span>
                )}
              </div>
              {profile.bio && (
                <p className="text-[#aaa] font-sans text-sm leading-relaxed mt-3 line-clamp-3">
                  {profile.bio}
                </p>
              )}
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-2 mt-4 text-center">
              <div className="rounded-lg bg-white/[0.03] border border-white/5 py-2">
                <div className="flex items-center justify-center gap-1 text-[#FFDA33]">
                  <Music2 size={11} />
                  <span className="text-sm font-mono">{profile.track_count ?? 0}</span>
                </div>
                <div className="text-[9px] font-mono text-[#666] uppercase tracking-widest mt-0.5">
                  Tracks
                </div>
              </div>
              <div className="rounded-lg bg-white/[0.03] border border-white/5 py-2">
                <div className="flex items-center justify-center gap-1 text-[#00CCFF]">
                  <Users size={11} />
                  <span className="text-sm font-mono">{profile.follower_count ?? 0}</span>
                </div>
                <div className="text-[9px] font-mono text-[#666] uppercase tracking-widest mt-0.5">
                  Followers
                </div>
              </div>
              <div className="rounded-lg bg-white/[0.03] border border-white/5 py-2">
                <div className="text-sm font-mono text-white">{profile.video_count ?? 0}</div>
                <div className="text-[9px] font-mono text-[#666] uppercase tracking-widest mt-0.5">
                  Videos
                </div>
              </div>
            </div>

            {/* Top tracks */}
            {tracks.length > 0 && (
              <div className="mt-6">
                <div className="text-[10px] font-mono text-[#666] uppercase tracking-widest mb-2">
                  Top Tracks
                </div>
                <div className="space-y-1.5">
                  {tracks.map((t, i) => (
                    <div
                      key={t.id}
                      className="flex items-center gap-3 rounded-md px-2 py-1.5 hover:bg-white/[0.04] transition-colors cursor-pointer"
                      onClick={() => { onClose?.(); navigate(`/track/${t.slug || t.id}`); }}
                      data-testid={`artist-drawer-track-${i}`}
                    >
                      <span className="text-[10px] font-mono text-[#666] w-4">{i + 1}</span>
                      <div className="w-9 h-9 rounded bg-[#111] overflow-hidden shrink-0">
                        {t.artwork && (
                          <img
                            src={t.artwork.startsWith('http') ? t.artwork : `${BACKEND_URL}/api/artwork/${t.artwork}`}
                            alt=""
                            className="w-full h-full object-cover"
                          />
                        )}
                      </div>
                      <span className="flex-1 text-sm text-white truncate">{t.title}</span>
                      {t.play_count != null && (
                        <span className="text-[10px] font-mono text-[#666]">
                          {t.play_count.toLocaleString()} plays
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* CTAs */}
            <div className="mt-6 flex gap-2">
              <Button
                onClick={gotoFullProfile}
                className="flex-1 bg-[#FFDA33] text-black hover:bg-[#FFE97A] font-['Chakra_Petch'] uppercase tracking-wider"
                data-testid="artist-drawer-view-profile"
              >
                View full profile <ExternalLink size={14} className="ml-1.5" />
              </Button>
            </div>
          </div>
        )}

        {!loading && !profile && userId && (
          <div className="px-5 py-12 text-center text-[#888] font-mono text-sm">
            Artist unavailable.
          </div>
        )}
      </aside>

      <style>{`
        @keyframes artistDrawerSlideIn {
          from { transform: translateX(100%); opacity: 0.6; }
          to   { transform: translateX(0);    opacity: 1; }
        }
      `}</style>
    </div>
  );
}
